/**
 * Camera.ts — Pan / zoom da camera com interpolacao suave (lerp).
 *
 * Trabalha em coordenadas do mundo (0..tamanhoMapa, default 3000).
 * Aplica a transformacao no worldContainer a cada frame.
 */

import type { PixiApp } from './PixiApp';

export class Camera {
  // Posicao atual (interpolada)
  x = 1500;
  y = 1500;
  zoom = 0.5;

  // Alvo (pra onde a camera ta indo)
  alvoX = 1500;
  alvoY = 1500;
  alvoZoom = 0.5;

  tamanhoMapa = 3000;

  constructor(private readonly pixi: PixiApp) {}

  iniciar(): void {
    this.pixi.addTicker((ticker) => this.atualizar(ticker.deltaTime));
  }

  setAlvo(x: number, y: number, zoom?: number): void {
    this.alvoX = clamp(x, 0, this.tamanhoMapa);
    this.alvoY = clamp(y, 0, this.tamanhoMapa);
    if (zoom !== undefined) {
      this.alvoZoom = clamp(zoom, 0.25, 2.5);
    }
  }

  private atualizar(deltaTime: number): void {
    const dt = deltaTime / 60;
    const lerp = Math.min(1, dt * 12);
    this.x += (this.alvoX - this.x) * lerp;
    this.y += (this.alvoY - this.y) * lerp;
    this.zoom += (this.alvoZoom - this.zoom) * lerp;

    const vp = this.pixi.viewport;
    this.pixi.worldContainer.scale.set(this.zoom);
    this.pixi.worldContainer.x = vp.w / 2 - this.x * this.zoom;
    this.pixi.worldContainer.y = vp.h / 2 - this.y * this.zoom;
  }

  /** Coordenadas do mundo a partir de coordenadas de tela. */
  telaParaMundo(sx: number, sy: number): { x: number; y: number } {
    const vp = this.pixi.viewport;
    return {
      x: (sx - vp.w / 2) / this.zoom + this.x,
      y: (sy - vp.h / 2) / this.zoom + this.y,
    };
  }

  /** Coordenadas de tela a partir de coordenadas do mundo. */
  mundoParaTela(wx: number, wy: number): { x: number; y: number } {
    const vp = this.pixi.viewport;
    return {
      x: (wx - this.x) * this.zoom + vp.w / 2,
      y: (wy - this.y) * this.zoom + vp.h / 2,
    };
  }
}

function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}
